import {FeatureCollection} from '@turf/turf';
import {buildOrDivide, getCameraBoundingBox, toIGNBBoxString} from '../../cesiumTools';
import {LayerDatasource, LayerLoadType, MapperViewer} from '../../types';

export class Building3dLayer implements LayerDatasource {

  id = 'building';
  name = 'Bâtiment';
  active = false;
  dataSource = new Cesium.GeoJsonDataSource(this.name);
  loadType = LayerLoadType.Manual;
  viewer: MapperViewer;
  showLabel = false;

  url = 'https://data.geopf.fr/wfs?version=2.0.0&SERVICE=WFS&REQUEST=GetFeature&OUTPUTFORMAT=json&COUNT=10000&TYPENAME=BDTOPO_V3:batiment&BBOX=';

  previouslyLoadedBbox = null;
  isLoading = false;

  constructor() {
  }

  onBeforePrint(): void {

  }

  onAfterPrint(): void {

  }

  initialize(viewer: MapperViewer): void {
    this.viewer = viewer;
    viewer.dataSources.add(this.dataSource);
  }

  remove(): void {
    this.viewer.dataSources.remove(this.dataSource);
  }

  loadData(forceReload = false): void {
    if (!this.dataSource) {
      console.error('Viewer not initialized');
      return;
    }

    const bbox = toIGNBBoxString(getCameraBoundingBox(this.viewer));

    if (this.previouslyLoadedBbox === bbox && !forceReload) {
      return;
    }

    this.previouslyLoadedBbox = bbox;

    const buildFn = (geojson) => {
      this.build3dBuilding(geojson);
    };

    if (bbox) {
      this.isLoading = true;

      const url = `${this.url}${bbox}`;
      fetch(url)
        .then(res => res.json())
        .then((geojson: FeatureCollection) => {
          buildOrDivide(geojson, bbox, this.url, buildFn, 5000).then(() => {
            this.isLoading = false;
          });
        }).catch(err => {
        console.error('[Building3dLayer] -> (error) -> ', err);
        this.isLoading = false;
      });
    }
  }

  build3dBuilding(geojson: FeatureCollection): void {
    geojson?.features?.forEach(bati => {
      if (!this.dataSource.entities.getById(bati.id)) {

        if ('coordinates' in bati.geometry) {
          const coord = this.normalizePolygonCoordinates(bati.geometry.coordinates).flat(5);
          this.dataSource.entities.add({
            id: bati.id,
            polygon: {
              hierarchy: {
                positions: Cesium.Cartesian3.fromDegreesArray(coord),
              },
              extrudedHeight: bati.properties.hauteur,
              height: 0,
              heightReference: Cesium.HeightReference.CLAMP_TO_GROUND,
              ...this.set3DBuildingMaterial(bati.properties.usage_1, .75),
            }
          });
        }
      }

      this.viewer.dataSources.lowerToBottom(this.dataSource);
    });


    this.setActive(true);
  }

  normalizePolygonCoordinates(coordinates: any[]): any[] {
    return coordinates.map((polygon) => {
      return polygon.map((ring) => {
        return ring.map((coord) => {
          return [coord[0], coord[1]];
        });
      });
    });
  }

  set3DBuildingMaterial(nature, opacity): any {
    switch (nature) {
      case 'Industriel': {
        return {
          material: Cesium.Color.LIGHTGRAY.withAlpha(opacity),
          outlineColor: Cesium.Color.LIGHTGRAY.darken(0.2, new Cesium.Color()),
          outline: true
        };
      }
      case 'Religieux': {
        return {
          material: Cesium.Color.HOTPINK.withAlpha(opacity),
          outlineColor: Cesium.Color.HOTPINK.darken(0.2, new Cesium.Color()),
          outline: true
        };
      }
      case 'Agricole': {
        return {
          material: Cesium.Color.YELLOW.withAlpha(opacity),
          outlineColor: Cesium.Color.YELLOW.darken(0.2, new Cesium.Color()),
          outline: true
        };
      }
      case 'Sportif': {
        return {
          material: Cesium.Color.LIMEGREEN.withAlpha(opacity),
          outlineColor: Cesium.Color.LIMEGREEN.darken(0.2, new Cesium.Color()),
          outline: true
        };
      }
      default: {
        return {
          material: Cesium.Color.GHOSTWHITE.withAlpha(opacity),
          outlineColor: Cesium.Color.GHOSTWHITE.darken(0.2, new Cesium.Color()),
          outline: true
        };
      }
    }
  }

  setActive(active: boolean): void {
    this.active = active;
    this.dataSource.show = active;
  }
}
